// Columna derecha del explorer de Cartera: la ficha de la asignación elegida y su única acción.
// El lambda solo reasigna la asignación ACTIVE, así que sobre una cerrada el botón queda apagado
// con el motivo escrito al lado en vez de fallar con un error del backend.
import type { ReactNode } from 'react';
import { Users } from 'lucide-react';
import { AssignmentStatusEnum } from '@fiado/type-kit/bin/loanCollectorAssignment/enums/AssignmentStatusEnum';
import { Badge, Button, CopyableId } from '@/kit';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { cn } from '@/lib/cn';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import {
    describeActor,
    describeAssignmentStatus,
    describeCollector,
    describeStore,
} from '../logic';
import type { AssignmentRow, ResolvedLabel } from '../types';

const FIELD_LABEL = 'text-[9.5px] font-bold uppercase tracking-[0.04em] text-muted-foreground';

const NO_PERMISSION_REASON = 'No tienes permiso para reasignar cartera.';

/** Por qué el botón de reasignar está apagado, o `null` cuando se puede reasignar. */
export function reassignBlockedReason(row: AssignmentRow, canReassign: boolean): string | null {
    if (!canReassign) return NO_PERMISSION_REASON;
    if (row.status !== AssignmentStatusEnum.ACTIVE) {
        return `Solo se puede reasignar la asignación activa; esta está ${describeAssignmentStatus(row.status).toLowerCase()}.`;
    }
    return null;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="min-w-0">
            <div className={FIELD_LABEL}>{label}</div>
            <div className="mt-0.5 text-[12px]">{children}</div>
        </div>
    );
}

/** Nombre resuelto, o el id con la leyenda de por qué no hay nombre. */
function Resolved({ label }: { label: ResolvedLabel }) {
    return (
        <>
            <span className={cn('break-words', !label.isName && 'font-mono text-[11px]')}>
                {label.text}
            </span>
            {label.note === null ? null : (
                <div className="text-[10px] italic text-muted-foreground">{label.note}</div>
            )}
        </>
    );
}

export interface AssignmentDetailProps {
    /** `null` pinta el estado vacío de la columna. */
    row: AssignmentRow | null;
    canReassign: boolean;
    onReassign: (row: AssignmentRow) => void;
}

export function AssignmentDetail({ row, canReassign, onReassign }: AssignmentDetailProps) {
    if (!row) {
        return (
            <MasterDetailEmpty
                icon={<Users className="size-10" aria-hidden />}
                title="Selecciona una asignación"
                sub="Elige una fila de la lista para ver su ficha y reasignarla."
            />
        );
    }

    const collector = describeCollector(
        row.collectorUserId,
        row.collectorDisplayName,
        row.collectorPresence,
    );
    const store = describeStore(row.storeId, row.storeName);
    const blockedReason = reassignBlockedReason(row, canReassign);

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="border-b border-border px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-[15px] font-bold leading-tight">
                        <Resolved label={collector} />
                    </h2>
                    <Badge
                        variant={
                            row.status === AssignmentStatusEnum.ACTIVE ? 'success' : 'secondary'
                        }
                    >
                        {describeAssignmentStatus(row.status)}
                    </Badge>
                </div>
                <div className="mt-2.5 flex flex-wrap gap-3">
                    <CopyableId label="Crédito" value={row.creditId} />
                    <CopyableId label="Asignación" value={row.assignmentId} />
                </div>
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(8rem,1fr))] gap-3 px-4 py-3">
                <Field label="Tienda">
                    <Resolved label={store} />
                </Field>
                <Field label="Asignado el">{formatDateTimeMx(row.assignedAt)}</Field>
                <Field label="Reasignada de">
                    {row.reassignedFromUserId === null ? (
                        <span className="text-muted-foreground">No viene de otro cobrador</span>
                    ) : (
                        <Resolved
                            label={describeCollector(
                                row.reassignedFromUserId,
                                row.reassignedFromDisplayName,
                                row.reassignedFromPresence,
                            )}
                        />
                    )}
                </Field>
                <Field label="Creada por">{describeActor(row.createdBy)}</Field>
                <Field label="Actualizada por">{describeActor(row.updatedBy)}</Field>
                <Field label="Creada el">{formatDateTimeMx(row.createdAt)}</Field>
                <Field label="Actualizada el">{formatDateTimeMx(row.updatedAt)}</Field>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-3">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={blockedReason !== null}
                    onClick={() => {
                        onReassign(row);
                    }}
                >
                    Reasignar a otro cobrador
                </Button>
                {blockedReason === null ? null : (
                    <span className="text-[11px] text-muted-foreground">{blockedReason}</span>
                )}
            </div>
        </div>
    );
}
